import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Skeleton } from '@/components/ui/skeleton';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { LEVEL_DESCRIPTION, LEVEL_LABEL } from '@/lib/roles';
import type { InvitationPreview } from '@/lib/types';

const schema = z
  .object({
    firstName: z.string().trim().min(1, 'Enter your first name').max(100),
    lastName: z.string().trim().min(1, 'Enter your last name').max(100),
    phone: z.string().trim().max(40).optional(),
    password: z.string().max(128),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { message: 'Passwords do not match', path: ['confirm'] });
/** Password fields are validated only when the tenant signs in with passwords. */
const withPassword = schema.refine((v) => v.password.length >= 10, { message: 'Use at least 10 characters', path: ['password'] });
type Values = z.infer<typeof schema>;

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const preview = useQuery({ queryKey: ['invitation', token], queryFn: () => api.invitations.preview(token), enabled: token.length >= 16, retry: false });

  return (
    <AuthLayout demo={false}
      title="You're invited"
      footer={
        <Link to="/login" className="hover:text-foreground underline underline-offset-4">
          Already have an account? Sign in
        </Link>
      }
    >
      {!token || token.length < 16 ? (
        <Alert variant="destructive">
          <AlertTitle>Invalid invitation link</AlertTitle>
          <AlertDescription>Open the link from your invitation email, or ask the person who invited you to send a new one.</AlertDescription>
        </Alert>
      ) : preview.isPending ? (
        <div className="grid gap-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : preview.isError ? (
        <Alert variant="destructive">
          <AlertTitle>This invitation can't be used</AlertTitle>
          <AlertDescription>{errorMessage(preview.error)}</AlertDescription>
        </Alert>
      ) : (
        <AcceptForm token={token} preview={preview.data} />
      )}
    </AuthLayout>
  );
}

function AcceptForm({ token, preview }: { token: string; preview: InvitationPreview }) {
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { branding } = useBranding();
  const passwordRequired = branding.loginMethods?.password ?? true;
  const form = useForm<Values>({ resolver: zodResolver(passwordRequired ? withPassword : schema), defaultValues: { firstName: '', lastName: '', phone: '', password: '', confirm: '' } });

  const finish = async (body: { firstName?: string; lastName?: string; password?: string; phone?: string | null }) => {
    setError(null);
    setBusy(true);
    try {
      const session = await api.invitations.accept(token, body);
      await adoptSession(session);
      navigate(preview.job ? `/jobs/${preview.job.id}` : '/jobs', { replace: true });
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const fe = err.fieldErrors();
        if (fe['password']) form.setError('password', { message: fe['password'] });
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const jobs = preview.jobs?.length ? preview.jobs : preview.job ? [{ id: preview.job.id, name: preview.job.name, role: preview.jobRole ?? 'user' }] : [];
  const level = preview.level ?? (preview.tenantRole === 'org_admin' ? 'super_user' : jobs.some((j) => j.role === 'admin') ? 'pm_admin' : 'pm_user');
  const scope =
    level === 'super_user' ? (
      <>
        <span className="text-foreground font-medium">{preview.tenant.name}</span> as a <span className="text-foreground font-medium">{LEVEL_LABEL.super_user}</span>
      </>
    ) : (
      <>
        <span className="text-foreground font-medium">{preview.tenant.name}</span> as a <span className="text-foreground font-medium">{LEVEL_LABEL[level]}</span> on{' '}
        {jobs.length === 1 ? (
          <span className="text-foreground font-medium">{jobs[0]?.name}</span>
        ) : (
          <>
            {jobs.length} jobs
          </>
        )}
      </>
    );

  return (
    <div className="grid gap-5">
      <p className="text-muted-foreground text-sm">
        <span className="text-foreground font-medium">{preview.email}</span> has been invited to join {scope}.
      </p>
      <div className="border px-3 py-2 text-xs">
        <p className="text-muted-foreground">{LEVEL_DESCRIPTION[level]}</p>
        {jobs.length > 1 && (
          <ul className="mt-2 grid gap-1">
            {jobs.map((j) => (
              <li key={j.id} className="flex items-center justify-between gap-3">
                <span className="text-foreground font-medium">{j.name}</span>
                <span className="text-muted-foreground">{LEVEL_LABEL[j.role === 'admin' ? 'pm_admin' : 'pm_user']}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <FormError message={error} />
      {preview.existingUser ? (
        <>
          <p className="text-sm">You already have an account. Accept to add this to your workspace, then sign in.</p>
          <Button className="w-full" disabled={busy} onClick={() => void finish({})}>
            {busy ? 'Accepting…' : 'Accept invitation'}
          </Button>
        </>
      ) : (
        <form
          onSubmit={form.handleSubmit((v) =>
            finish({ firstName: v.firstName, lastName: v.lastName, phone: v.phone?.trim() ? v.phone.trim() : null, ...(passwordRequired ? { password: v.password } : {}) }),
          )}
          className="grid gap-4"
          noValidate
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" htmlFor="firstName" error={form.formState.errors.firstName?.message}>
              <Input id="firstName" autoComplete="given-name" autoFocus {...form.register('firstName')} />
            </Field>
            <Field label="Last name" htmlFor="lastName" error={form.formState.errors.lastName?.message}>
              <Input id="lastName" autoComplete="family-name" {...form.register('lastName')} />
            </Field>
          </div>
          <Field label="Mobile number" htmlFor="phone" description={branding.loginMethods?.smsCode ? 'Optional. Lets you sign in with a text-message code.' : 'Optional.'} error={form.formState.errors.phone?.message}>
            <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(407) 555-0123" {...form.register('phone')} />
          </Field>
          {passwordRequired ? (
            <>
              <Field label="Password" htmlFor="password" description="At least 10 characters." error={form.formState.errors.password?.message}>
                <PasswordInput id="password" autoComplete="new-password" aria-invalid={!!form.formState.errors.password} {...form.register('password')} />
              </Field>
              <Field label="Confirm password" htmlFor="confirm" error={form.formState.errors.confirm?.message}>
                <PasswordInput id="confirm" autoComplete="new-password" capsLockHint={false} aria-invalid={!!form.formState.errors.confirm} {...form.register('confirm')} />
              </Field>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">This organization signs in with one-time codes sent by email or text, so you do not need a password.</p>
          )}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      )}
    </div>
  );
}
